import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import { useSocket } from '../../context/SocketContext';
import api from '../../utils/api';
import { FiPlus, FiMinus, FiShoppingCart, FiSearch, FiClock, FiCheckCircle } from 'react-icons/fi';

function StaffOrders() {
  const { alert } = useModal();
  const socket = useSocket();
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dietaryFilter, setDietaryFilter] = useState('all'); // all | veg | nonveg
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [showImages, setShowImages] = useState(false); // Toggle for images (default hidden)
  const [placedOrders, setPlacedOrders] = useState([]); // Flattened order items with statuses

  // Initial data
  useEffect(() => {
    fetchTables();
    fetchCategories();
    fetchMenuItems();
  }, []);

  // When a table is selected, load its active orders
  useEffect(() => {
    if (selectedTable) {
      fetchPlacedOrders();
    } else {
      setPlacedOrders([]);
    }
  }, [selectedTable]);

  // Listen for real-time order updates from KDS / POS (same event as admin POS)
  useEffect(() => {
    if (!socket) return;

    const handleOrderUpdated = () => {
      if (selectedTable) {
        fetchPlacedOrders();
      }
      fetchTables();
    };

    socket.on('order-updated', handleOrderUpdated);

    return () => {
      socket.off('order-updated', handleOrderUpdated);
    };
  }, [socket, selectedTable]);

  const fetchTables = async () => {
    try {
      const response = await api.get('/api/tables');
      setTables(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
      setTables([]);
    }
  };

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/menu?availableOnly=true');
      setMenuItems(response.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/menu/categories');
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchPlacedOrders = async () => {
    if (!selectedTable) return;

    try {
      const response = await api.get(`/api/pos/tables/${selectedTable}/orders`);
      const orders = response.data?.orders || [];

      // Show only active/unpaid orders for this table
      const activeOrders = orders.filter(order =>
        order.payment_status !== 'paid' && order.status !== 'cancelled'
      );

      const allOrderItems = [];
      activeOrders.forEach(order => {
        if (order.order_items && order.order_items.length > 0) {
          order.order_items.forEach(item => {
            allOrderItems.push({
              ...item,
              orderId: order.id,
              orderNumber: order.order_number,
              orderStatus: order.status,
              itemStatus: item.status || 'pending',
              orderCreatedAt: order.created_at,
            });
          });
        }
      });

      setPlacedOrders(allOrderItems);
    } catch (error) {
      console.error('Failed to fetch placed orders:', error);
      setPlacedOrders([]);
    }
  };

  const addToCart = (item) => {
    // Default behaviour: merge same dish without special instructions (Spring Roll x2)
    // Only create a separate line when we later add a special instruction.
    setCart((prev) => {
      const idx = prev.findIndex(
        (line) =>
          line.id === item.id &&
          (!line.specialInstructions || line.specialInstructions.trim() === '')
      );

      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          quantity: updated[idx].quantity + 1,
        };
        return updated;
      }

      return [
        ...prev,
        {
          ...item,
          quantity: 1,
          apcUnitPrice: item.is_apc ? Number(item.price) || 0 : null,
          specialInstructions: '',
        },
      ];
    });
  };

  const getCartItemUnitPrice = (item) => {
    if (!item?.is_apc) {
      return Number(item.price) || 0;
    }

    if (item.apcUnitPrice === '' || item.apcUnitPrice === null || item.apcUnitPrice === undefined) {
      return Number(item.price) || 0;
    }

    const apcPrice = Number(item.apcUnitPrice);
    if (!Number.isFinite(apcPrice) || apcPrice < 0) {
      return Number(item.price) || 0;
    }

    return apcPrice;
  };

  const updateApcUnitPrice = (index, value) => {
    setCart((prev) =>
      prev.map((item, i) => {
        if (i !== index) {
          return item;
        }

        return {
          ...item,
          apcUnitPrice: value === '' ? '' : value
        };
      })
    );
  };

  const updateQuantity = (index, quantity) => {
    if (quantity <= 0) {
      setCart((prev) => prev.filter((_, i) => i !== index));
    } else {
      setCart((prev) =>
        prev.map((item, i) => (i === index ? { ...item, quantity } : item))
      );
    }
  };

  const getCartQuantityForItem = (itemId) => {
    return cart
      .filter((line) => line.id === itemId)
      .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  };

  const decrementFromCard = (itemId) => {
    setCart((prev) => {
      const preferredIndex = prev.findIndex(
        (line) => line.id === itemId && (!line.specialInstructions || line.specialInstructions.trim() === '')
      );

      const fallbackIndex = prev.findIndex((line) => line.id === itemId);
      const targetIndex = preferredIndex !== -1 ? preferredIndex : fallbackIndex;

      if (targetIndex === -1) return prev;

      const targetLine = prev[targetIndex];
      const nextQty = Number(targetLine.quantity || 0) - 1;

      if (nextQty <= 0) {
        return prev.filter((_, i) => i !== targetIndex);
      }

      return prev.map((line, i) => (i === targetIndex ? { ...line, quantity: nextQty } : line));
    });
  };

  const handlePlaceOrder = async () => {
    if (!selectedTable) {
      await alert('Please select a table', 'Table Required', 'warning');
      return;
    }
    if (cart.length === 0) {
      await alert('Cart is empty', 'Empty Cart', 'warning');
      return;
    }
    if (placingOrder) {
      return; // Prevent double submission
    }

    setPlacingOrder(true);

    try {
      console.log('Starting order placement...', { selectedTable, cartLength: cart.length });

      // Prepare order data with correct field names (backend expects camelCase)
      const orderData = {
        tableId: selectedTable,
        guestType: 'restaurant',
        orderType: 'dine_in',
        orderSource: 'staff',
        items: cart.map(item => ({
          menuItemId: item.id, // Backend expects camelCase, not menu_item_id
          quantity: item.quantity,
          specialInstructions: item.specialInstructions || '',
          ...(item.is_apc ? { unitPrice: getCartItemUnitPrice(item) } : {})
        }))
      };

      console.log('Placing order with data:', JSON.stringify(orderData, null, 2));
      const response = await api.post('/api/orders', orderData);
      console.log('Order placed successfully:', response.data);
      await alert('Order placed successfully!', 'Success', 'success');
      setCart([]);
      // Refresh tables and placed orders to show updated status
      await fetchTables();
      await fetchPlacedOrders();
    } catch (error) {
      console.error('Failed to place order:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to place order';
      await alert(`Failed to place order: ${errorMessage}\n\nPlease check the browser console for more details.`, 'Error', 'error');
    } finally {
      setPlacingOrder(false);
    }
  };

  const normalizeSearchValue = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

  const filteredMenuItems = menuItems.filter((item) => {
    const normalizedTerm = normalizeSearchValue(searchTerm);
    const nameNormalized = normalizeSearchValue(item.name);
    const nameWords = nameNormalized.length > 0 ? nameNormalized.split(' ') : [];

    const matchesCategory = normalizedTerm
      ? true
      : (!selectedCategory || item.category_id === selectedCategory);

    const isVegItem = item.is_vegetarian === true;
    const isDietaryCommon = item.is_dietary_common === true;
    const matchesDietary =
      dietaryFilter === 'all' ||
      (dietaryFilter === 'veg' && (isVegItem || isDietaryCommon)) ||
      (dietaryFilter === 'nonveg' && (!isVegItem || isDietaryCommon));

    const startsWithName = normalizedTerm && nameNormalized.startsWith(normalizedTerm);
    const startsWithAnyWord = normalizedTerm && nameWords.some((word) => word.startsWith(normalizedTerm));
    const matchesSearch = !normalizedTerm || startsWithName || startsWithAnyWord;

    return matchesCategory && matchesDietary && matchesSearch;
  });

  const sortedTables = [...tables].sort((a, b) => {
    const aLabel = String(a?.table_number ?? '').trim();
    const bLabel = String(b?.table_number ?? '').trim();
    const natural = aLabel.localeCompare(bLabel, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
    if (natural !== 0) return natural;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''), undefined, { numeric: true });
  });

  const total = cart.reduce((sum, item) => sum + (getCartItemUnitPrice(item) * item.quantity), 0);
  const cgst = total * 0.025; // 2.5% CGST
  const sgst = total * 0.025; // 2.5% SGST
  const tax = cgst + sgst; // Total tax = 5%
  const grandTotal = total + tax;

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    return `${h}:${minutes} ${ampm}`;
  };

  const getItemStatusMeta = (rawStatus) => {
    const status = String(rawStatus || 'pending').toLowerCase();

    if (status === 'served') {
      return {
        label: 'Served',
        className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800',
      };
    }

    if (status === 'ready') {
      return {
        label: 'Ready',
        className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800',
      };
    }

    if (status === 'preparing') {
      return {
        label: 'Preparing',
        className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200',
      };
    }

    if (status === 'confirmed') {
      return {
        label: 'Confirmed',
        className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-800 border border-indigo-200',
      };
    }

    if (status === 'cancelled') {
      return {
        label: 'Cancelled',
        className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-800 border border-red-200',
      };
    }

    return {
      label: 'Pending',
      className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-800 border border-yellow-200',
    };
  };

  const clubbedPlacedOrders = placedOrders.reduce((acc, item) => {
    const name = item.menu_items?.name || item.name || 'Item';
    const key = [
      item.menu_item_id || item.id || name,
      String(item.special_instructions || '').trim().toLowerCase()
    ].join('|');

    if (!acc[key]) {
      acc[key] = {
        key,
        name,
        totalQty: 0,
        entries: [],
      };
    }

    const qty = Number(item.quantity || 0);
    const status = item.itemStatus || item.status || 'pending';
    const itemTime = item.created_at || item.orderCreatedAt;

    acc[key].totalQty += qty;
    acc[key].entries.push({
      qty,
      status,
      itemTime,
      orderNumber: item.orderNumber
    });

    return acc;
  }, {});

  const clubbedPlacedOrdersList = Object.values(clubbedPlacedOrders);

  return (
    <Layout title="Place Order">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* Left Section - Menu */}
        <div className="md:col-span-3 space-y-6">
          {/* Menu Items */}
          <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Menu Items</h2>
              <div className="flex gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search menu items across all categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
                <button
                  onClick={() => setShowImages(!showImages)}
                  className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    showImages
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={showImages ? 'Hide images for faster selection' : 'Show images for visual confirmation'}
                >
                  {showImages ? '📷 Hide' : '📷 Show'}
                </button>
              </div>
            </div>

            {/* Category chips - tablet-first, horizontal quick access */}
            <div className="mb-4">
              <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                <button
                  type="button"
                  onClick={() => setSelectedCategory(null)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                    !selectedCategory
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Veg / Non-veg filter */}
            <div className="mb-4">
              <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                <button
                  type="button"
                  onClick={() => setDietaryFilter('all')}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                    dietaryFilter === 'all'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All Items
                </button>
                <button
                  type="button"
                  onClick={() => setDietaryFilter('veg')}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                    dietaryFilter === 'veg'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Veg
                </button>
                <button
                  type="button"
                  onClick={() => setDietaryFilter('nonveg')}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                    dietaryFilter === 'nonveg'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Non-Veg
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading menu items...</p>
              </div>
            ) : filteredMenuItems.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">{searchTerm || dietaryFilter !== 'all' ? 'No items found for selected filters' : 'No menu items available'}</p>
              </div>
            ) : (
              <div className={`grid gap-4 ${
                showImages 
                  ? 'grid-cols-2 md:grid-cols-3' 
                  : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
              }`}>
                {filteredMenuItems.map(item => (
                  (() => {
                    const cardQty = getCartQuantityForItem(item.id);
                    return (
                  <div 
                    key={item.id} 
                    className={`border border-gray-200 rounded-xl hover:shadow-md transition-all hover:border-gray-300 flex flex-col ${
                      showImages ? 'p-4' : 'p-3'
                    }`}
                  >
                    {showImages ? (
                      <>
                        {item.image_url ? (
                          <div className="w-full h-32 bg-gray-100 rounded-lg mb-3 overflow-hidden flex-shrink-0">
                            <img 
                              src={item.image_url} 
                              alt={item.name} 
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-32 bg-gray-100 rounded-lg mb-3 flex items-center justify-center flex-shrink-0">
                            <span className="text-gray-400 text-xs">No Image</span>
                          </div>
                        )}
                        <h3 className="font-semibold text-gray-900 mb-1 text-sm leading-tight break-words min-h-[2.2rem]">
                          {item.name}
                        </h3>
                        {item.description && (
                          <p className="text-xs text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                        )}
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-bold text-gray-900 text-sm">
                            ₹{parseFloat(item.price).toFixed(2)}
                          </span>
                          {item.is_vegetarian && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Veg</span>
                          )}
                        </div>
                        {cardQty > 0 ? (
                          <div className="w-full flex items-center justify-between bg-gray-900 text-white rounded-lg py-2 px-2 text-sm">
                            <button
                              onClick={() => decrementFromCard(item.id)}
                              className="p-1 rounded hover:bg-gray-800 transition-colors"
                              title="Decrease quantity"
                            >
                              <FiMinus />
                            </button>
                            <span className="font-semibold">{cardQty}</span>
                            <button
                              onClick={() => addToCart(item)}
                              className="p-1 rounded hover:bg-gray-800 transition-colors"
                              title="Increase quantity"
                            >
                              <FiPlus />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => addToCart(item)}
                            className="w-full bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium transition-all shadow-sm hover:shadow-md touch-manipulation py-2 text-sm"
                          >
                            <FiPlus className="inline mr-1" /> Add to Cart
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <h3 className="font-semibold text-gray-900 mb-1 text-xs sm:text-sm leading-tight break-words min-h-[2.1rem]">
                          {item.name}
                        </h3>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-bold text-gray-900 text-xs sm:text-sm">
                            ₹{parseFloat(item.price).toFixed(2)}
                          </span>
                          {item.is_vegetarian && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Veg</span>
                          )}
                        </div>
                        {cardQty > 0 ? (
                          <div className="w-full flex items-center justify-between bg-gray-900 text-white rounded-lg py-1.5 px-2 text-xs sm:text-sm">
                            <button
                              onClick={() => decrementFromCard(item.id)}
                              className="p-1 rounded hover:bg-gray-800 transition-colors"
                              title="Decrease quantity"
                            >
                              <FiMinus />
                            </button>
                            <span className="font-semibold">{cardQty}</span>
                            <button
                              onClick={() => addToCart(item)}
                              className="p-1 rounded hover:bg-gray-800 transition-colors"
                              title="Increase quantity"
                            >
                              <FiPlus />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => addToCart(item)}
                            className="w-full bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium transition-all shadow-sm hover:shadow-md touch-manipulation py-1.5 text-xs sm:text-sm"
                          >
                            <FiPlus className="inline mr-1" /> Add to Cart
                          </button>
                        )}
                      </>
                    )}
                  </div>
                    );
                  })()
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Cart + Active Orders */}
        <div className="space-y-6 md:col-span-2">
          {/* Table Selection */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Order Details</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Table *</label>
                <div className="flex gap-2 flex-wrap max-h-32 overflow-y-auto pr-1">
                  {sortedTables.map((table) => (
                    <button
                      key={table.id}
                      type="button"
                      onClick={() => setSelectedTable(table.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedTable === table.id
                          ? 'bg-gray-900 text-white'
                          : table.status === 'occupied'
                          ? 'bg-red-100 text-red-800 border border-red-300'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {table.table_number}
                      {table.status === 'occupied' && ' ⚠'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Cart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:sticky md:top-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Order Cart</h2>
            {cart.length === 0 ? (
              <div className="text-center py-12">
                <FiShoppingCart className="text-4xl text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Your cart is empty</p>
                <p className="text-gray-400 text-xs mt-1">Add items from the menu</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                  {cart.map((item, index) => (
                    <div key={`${item.id}-${index}`} className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                      <div className="space-y-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-gray-900 whitespace-normal break-words leading-snug">{item.name}</p>
                          <p className="text-xs text-gray-600">₹{getCartItemUnitPrice(item).toFixed(2)} each</p>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 shrink-0">
                            <button
                              onClick={() => updateQuantity(index, item.quantity - 1)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                            >
                              <FiMinus className="text-sm" />
                            </button>
                            <span className="w-8 text-center font-semibold text-sm">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(index, item.quantity + 1)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                            >
                              <FiPlus className="text-sm" />
                            </button>
                          </div>
                          <p className="font-bold text-sm text-right shrink-0">₹{(getCartItemUnitPrice(item) * item.quantity).toFixed(2)}</p>
                        </div>
                        {item.is_apc && (
                          <div className="flex items-center justify-between gap-3 mt-1">
                            <span className="text-[11px] font-semibold text-indigo-600">APC unit price</span>
                            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-2 py-1">
                              <span className="text-xs text-gray-500">₹</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.apcUnitPrice ?? ''}
                                onChange={(e) => updateApcUnitPrice(index, e.target.value)}
                                className="w-20 text-xs text-right focus:outline-none"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="space-y-1 pt-1">
                        <span className="text-[11px] font-semibold text-gray-600">
                          Special instructions (optional)
                        </span>
                        <textarea
                          rows={2}
                          placeholder="E.g. no onion, extra spicy, less oil..."
                          value={item.specialInstructions || ''}
                          onChange={(e) =>
                            setCart(
                              cart.map((c, i) =>
                                i === index ? { ...c, specialInstructions: e.target.value } : c
                              )
                            )
                          }
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900 resize-none bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold">₹{total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">CGST (2.5%):</span>
                    <span className="font-semibold">₹{cgst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">SGST (2.5%):</span>
                    <span className="font-semibold">₹{sgst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg pt-2 border-t border-gray-200">
                    <span className="font-bold text-gray-900">Total:</span>
                    <span className="font-bold text-gray-900">₹{grandTotal.toFixed(2)}</span>
                  </div>
                  <button
                    onClick={handlePlaceOrder}
                    disabled={!selectedTable || cart.length === 0 || placingOrder}
                    className="w-full bg-gray-900 text-white py-3 rounded-xl hover:bg-gray-800 disabled:hover:bg-gray-900 flex items-center justify-center gap-2 font-medium shadow-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                  >
                    {placingOrder ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Placing Order...</span>
                      </>
                    ) : (
                      <>
                        <FiShoppingCart className="text-lg" /> Place Order
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Active Orders for Selected Table */}
          {selectedTable && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                  Active Orders for this Table
                </h2>
                <span className="text-xs text-gray-500">
                  {clubbedPlacedOrdersList.length > 0 ? `${clubbedPlacedOrdersList.length} item(s)` : 'No active items'}
                </span>
              </div>
              {clubbedPlacedOrdersList.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No active orders for this table yet. Place an order to see its status here.
                </p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {clubbedPlacedOrdersList.map((clubbedItem) => (
                    (() => {
                      const uniqueStatuses = [...new Set(clubbedItem.entries.map((entry) => String(entry.status || 'pending').toLowerCase()))];
                      const statusMeta = getItemStatusMeta(uniqueStatuses.length === 1 ? uniqueStatuses[0] : 'pending');
                      return (
                    <div
                      key={clubbedItem.key}
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate flex items-center gap-2">
                          <span>{clubbedItem.name}</span>
                          <span className="text-xs text-gray-600">x{clubbedItem.totalQty}</span>
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {clubbedItem.entries.map((entry, entryIdx) => (
                            <span key={entryIdx} className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                              {entry.qty} {String(entry.status || 'pending').toLowerCase()} • {formatTime(entry.itemTime)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={statusMeta.className}>
                          {statusMeta.label === 'Ready' || statusMeta.label === 'Served' ? (
                            <FiCheckCircle className="h-3 w-3" />
                          ) : (
                            <FiClock className="h-3 w-3" />
                          )}
                          {statusMeta.label}
                        </span>
                      </div>
                    </div>
                      );
                    })()
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default StaffOrders;



